import { FC, useEffect, useRef, useState } from "react";
import { Feature, Map, Overlay, View } from "ol";

import { MapboxVectorLayer } from "ol-mapbox-style";
import VectorSource from "ol/source/Vector";
import { fromLonLat, transform } from "ol/proj";
import VectorLayer from "ol/layer/Vector";
import {MouseWheelZoom} from 'ol/interaction';


import "ol/ol.css";
import { Point } from "ol/geom";
import { Fill, Stroke, Style } from "ol/style";
import CircleStyle from "ol/style/Circle";
import {
  getBottomLeft,
  getBottomRight,
  getTopLeft,
  getTopRight,
} from "ol/extent";
import { Coordinate } from "ol/coordinate";
import { displayNumber } from "./App";
import { Button, Spin, Tooltip } from "antd";

import { DoubleRightOutlined, InfoCircleOutlined, LoadingOutlined } from "@ant-design/icons";
import { orderBy } from "lodash";
import axios from "axios";

// Utility function to safely get coordinates from a feature
const getFeatureCoordinates = (feature: Feature): number[] | null => {
  try {
    const featureGeometry = feature.getGeometry();
    if (featureGeometry && featureGeometry.getType() === 'Point') {
      const pointGeometry = featureGeometry as Point;
      if (pointGeometry && typeof pointGeometry.getCoordinates === 'function') {
        const coords = pointGeometry.getCoordinates();
        if (coords && Array.isArray(coords) && coords.length >= 2) {
          return coords;
        }
      }
    }
  } catch (error) {
    console.warn('Error getting coordinates from feature:', error);
  }
  return null;
};

const styles: Record<string, any> = {
  geoMarker_green: [
    // Invisible larger circle for better touch targeting
    new Style({
      image: new CircleStyle({
        radius: 15,
        fill: new Fill({ color: "rgba(0, 0, 0, 0)" }), // Transparent
      }),
      zIndex: 0,
    }),
    // Visible marker
    new Style({
      image: new CircleStyle({
        radius: 5,
        fill: new Fill({ color: "#7ED957" }),
        stroke: new Stroke({
          color: "white",
          width: 2,
        }),
      }),
      zIndex: 1,
    }),
  ],
  geoMarker_red: [
    // Invisible larger circle for better touch targeting
    new Style({
      image: new CircleStyle({
        radius: 15,
        fill: new Fill({ color: "rgba(0, 0, 0, 0)" }), // Transparent
      }),
      zIndex: 0,
    }),
    // Visible marker
    new Style({
      image: new CircleStyle({
        radius: 5,
        fill: new Fill({ color: "red" }),
        stroke: new Stroke({
          color: "white",
          width: 2,
        }),
      }),
      zIndex: 1,
    }),
  ],
  geoMarker_blue: [
    // Invisible larger circle for better touch targeting
    new Style({
      image: new CircleStyle({
        radius: 15,
        fill: new Fill({ color: "rgba(0, 0, 0, 0)" }), // Transparent
      }),
      zIndex: 0,
    }),
    // Visible marker
    new Style({
      image: new CircleStyle({
        radius: 5,
        fill: new Fill({ color: "#001484" }),
        stroke: new Stroke({
          color: "white",
          width: 2,
        }),
      }),
      zIndex: 1,
    }),
  ],
  geoMarkerSelected_blue: [
    // Invisible larger circle for better touch targeting
    new Style({
      image: new CircleStyle({
        radius: 15,
        fill: new Fill({ color: "rgba(0, 0, 0, 0)" }), // Transparent
      }),
      zIndex: 0,
    }),
    // Visible marker
    new Style({
      image: new CircleStyle({
        radius: 5,
        fill: new Fill({ color: "#001484" }),
        stroke: new Stroke({
          color: "white",
          width: 2,
        }),
      }),
      zIndex: 2,
    }),
    // Selection indicator
    new Style({
      image: new CircleStyle({
        radius: 9,
        fill: new Fill({ color: "rgba(0, 20, 130, 0.3)" }),
      }),
      zIndex: 1,
    }),
  ],
  geoMarkerSelected_green: [
    // Invisible larger circle for better touch targeting
    new Style({
      image: new CircleStyle({
        radius: 15,
        fill: new Fill({ color: "rgba(0, 0, 0, 0)" }), // Transparent
      }),
      zIndex: 0,
    }),
    // Visible marker
    new Style({
      image: new CircleStyle({
        radius: 5,
        fill: new Fill({ color: "#7ED957" }),
        stroke: new Stroke({
          color: "white",
          width: 2,
        }),
      }),
      zIndex: 2,
    }),
    // Selection indicator
    new Style({
      image: new CircleStyle({
        radius: 9,
        fill: new Fill({ color: "rgba(0, 20, 130, 0.3)" }),
      }),
      zIndex: 1,
    }),
  ],
  geoMarkerSelected_red: [
    // Invisible larger circle for better touch targeting
    new Style({
      image: new CircleStyle({
        radius: 15,
        fill: new Fill({ color: "rgba(0, 0, 0, 0)" }), // Transparent
      }),
      zIndex: 0,
    }),
    // Visible marker
    new Style({
      image: new CircleStyle({
        radius: 5,
        fill: new Fill({ color: "red" }),
        stroke: new Stroke({
          color: "white",
          width: 2,
        }),
      }),
      zIndex: 2,
    }),
    // Selection indicator
    new Style({
      image: new CircleStyle({
        radius: 9,
        fill: new Fill({ color: "rgba(0, 20, 130, 0.3)" }),
      }),
      zIndex: 1,
    }),
  ],
};

const MapComponent: FC<{
  onParcellesChanged: (parcelles: any[]) => void;
  onSelectParcelle: (parcelle: string | null, commune: string | null) => void;
  center: string;
  marker: string;
  parcelleDetails: string | null;
  parcelleDetailsNoCommune: string | null;
  filters: any;
  showSMSModal: any;
  smsToken: any;
  searchLocationAuthenticated: any;
  setSearchLocationAuthenticated: any;
  remainingCredits: any;
  setRemainingCredits: any;
  displayPrice: any;
  displayContactUsModal: any;
  onParcelleDetailsAccess: any;
  setOpenTransactionList: any;
  openTransactionList: any;
  setOpenFilters: any;
  recenterMap: boolean;
  setLoadingParcellesList: any;
  loadingParcellesList: boolean;
}> = ({
  parcelleDetails,
  parcelleDetailsNoCommune,
  center,
  marker,
  onParcellesChanged,
  onSelectParcelle,
  filters,
  showSMSModal,
  smsToken,
  searchLocationAuthenticated,
  setSearchLocationAuthenticated,
  setRemainingCredits,
  displayPrice,
  displayContactUsModal,
  remainingCredits,
  onParcelleDetailsAccess,
  setOpenTransactionList,
  openTransactionList,
  setOpenFilters,
  recenterMap,
  setLoadingParcellesList,
  loadingParcellesList
}) => {
  const detailPopup = useRef(null);
  const titlePopup = useRef(null);

  const detailOverlay = useRef<Overlay | null>(null);
  const titleOverlay = useRef<Overlay | null>(null);

  const [hoveredParcelle, setHoveredParcelle] = useState("");
  const [parcelles, setParcelles] = useState<any[]>([]);
  const [selectedParcelle, setSelectedParcelle] = useState(null);
  const [selectedParcelleNoCommune, setSelectedParcelleNoCommune] = useState(null);
  const [parcelleStats, setParcelleStats] = useState<any>(null);
  const [corners, setCorners] = useState<any>({});
  const [zoom, setZoom] = useState(16);
  const [centerCoordinate, setCenterCoordinate] = useState<Coordinate>([
    6.1432, 46.2044,
  ]);
  const map = useRef(new Map());
  const vectorSource = useRef(new VectorSource({ features: [] }));

  useEffect(() => {
    if (center === "") return;
    const c = center.split("|");
    setCenterCoordinate([+c[0], +c[1]]);
    reloadMarker();
    map.current.getView().setCenter(fromLonLat([+c[0], +c[1]]));
  }, [center, recenterMap]);

  useEffect(() => {
    const fetchParcelleStats = async (
      noParcelle: string,
      noCommune: string
    ) => {
      const response = await axios.get(
        `https://melcarnebackend.digipulse.ch/get_map_stats_parcelle_public?NoCommune=${noCommune}&NoParcelle=${noParcelle}&filters=${JSON.stringify(
          filters
        )}`
      );
      console.log(filters);

      setParcelleStats(response.data);
    };

    if (detailOverlay.current && parcelleDetails && parcelleDetailsNoCommune) {
      const p: any = parcelles.find(
        (p: any) => p?.NoParcelle === parcelleDetails && p?.NoCommune === parcelleDetailsNoCommune
      );

      if (p) {
        setSelectedParcelle(p?.NoParcelle);
        setSelectedParcelleNoCommune(p?.NoCommune);

        fetchParcelleStats(p?.NoParcelle, p?.NoCommune);

        const coordinate = fromLonLat(p.position_sur_map.coordinates);
        detailOverlay.current.setPosition(coordinate);
        vectorSource.current.forEachFeature((feature: Feature) => {
          feature.set(
            "type",
            feature.get("name") === `${parcelleDetails}#${parcelleDetailsNoCommune}`
              ? "geoMarkerSelected"
              : "geoMarker"
          );
        });
      }
    }
  }, [parcelleDetails, parcelleDetailsNoCommune, parcelles, setParcelleStats]);

  useEffect(() => {
    const searchParcelle = async (corners: any) => {
      const centerLon =
        (corners.bl[0] + corners.br[0] + corners.tl[0] + corners.tr[0]) / 4;
      const centerLat =
        (corners.bl[1] + corners.br[1] + corners.tl[1] + corners.tr[1]) / 4;

      const metersToDegrees = (
        meters: number,
        atLat: number
      ): { latDelta: number; lonDelta: number } => {
        const latDelta = meters / 111320; // Approximation for latitude
        const lonDelta = meters / (111320 * Math.cos(atLat * (Math.PI / 180))); // Adjust for longitude at the given latitude
        return { latDelta, lonDelta };
      };

      const { latDelta, lonDelta } = metersToDegrees(500, centerLat);

      const newCorners = {
        bl: [centerLon - lonDelta, centerLat - latDelta],
        br: [centerLon + lonDelta, centerLat - latDelta],
        tl: [centerLon - lonDelta, centerLat + latDelta],
        tr: [centerLon + lonDelta, centerLat + latDelta],
      };

      // Function to calculate the approximate area of a bounding box
      const calculateBBoxArea = (corners: {
        bl: number[];
        br: number[];
        tl: number[];
        tr: number[];
      }): number => {
        const minLat = Math.min(
          corners.bl[1],
          corners.br[1],
          corners.tl[1],
          corners.tr[1]
        );
        const maxLat = Math.max(
          corners.bl[1],
          corners.br[1],
          corners.tl[1],
          corners.tr[1]
        );
        const minLon = Math.min(
          corners.bl[0],
          corners.br[0],
          corners.tl[0],
          corners.tr[0]
        );
        const maxLon = Math.max(
          corners.bl[0],
          corners.br[0],
          corners.tl[0],
          corners.tr[0]
        );

        const latDifference = maxLat - minLat;
        const lonDifference = maxLon - minLon;

        // Use the latitude in radians for longitude adjustment
        return (
          latDifference * lonDifference * Math.cos(centerLat * (Math.PI / 180))
        );
      };

      // Calculate areas
      const originalBBoxArea = calculateBBoxArea(corners);
      const newBBoxArea = calculateBBoxArea(newCorners);

      // Use newCorners only if its area is smaller
      const finalBBox = newBBoxArea < originalBBoxArea ? newCorners : corners;

      // Append the final bounding box to API params
      const apiParams = new URLSearchParams();
      apiParams.append("bl_lon", finalBBox.bl[0].toString());
      apiParams.append("bl_lat", finalBBox.bl[1].toString());
      apiParams.append("br_lon", finalBBox.br[0].toString());
      apiParams.append("br_lat", finalBBox.br[1].toString());
      apiParams.append("tl_lon", finalBBox.tl[0].toString());
      apiParams.append("tl_lat", finalBBox.tl[1].toString());
      apiParams.append("tr_lon", finalBBox.tr[0].toString());
      apiParams.append("tr_lat", finalBBox.tr[1].toString());
      apiParams.append("limit", "20000");
      apiParams.append("offset", "0");
      apiParams.append("filter", JSON.stringify(filters));

      const response = await axios.get(
        smsToken && searchLocationAuthenticated
          ? `https://melcarnebackend.digipulse.ch/search_by_id_sms_authenticated?smsToken=${smsToken}&record_id=${searchLocationAuthenticated}&${apiParams.toString()}`
          : `https://melcarnebackend.digipulse.ch/search_by_location_public?${apiParams.toString()}`
      );

      if (response.data.status === "error") {
        displayContactUsModal();
      } else {
        const parcellesData =
          smsToken && searchLocationAuthenticated
            ? [...response.data.records, ...response.data.blurry_records]
            : response.data;

        if (smsToken && searchLocationAuthenticated) {
          console.log(response.data);
          setRemainingCredits(response.data.remaining_credits);
        }

        const parcelles = orderBy(
          parcellesData.filter((p: any) => !!p.position_sur_map),
          ["NoParcelle"]
        ) as any[];

        vectorSource.current.clear();
        parcelles.forEach(
          ({
            position_sur_map: { coordinates },
            NoParcelle,
            NoCommune,
            Adresse,
            NPA,
            CommuneAdresse,
            SVB_Verifiee,
            status,
          }: any) => {
            vectorSource.current.addFeature(
              new Feature({
                type: "geoMarker",
                geometry: new Point(
                  fromLonLat([coordinates[0], coordinates[1]])
                ),
                name: `${NoParcelle}#${NoCommune}`,
                address: `${Adresse}, ${NPA} ${CommuneAdresse}`,
                svb: status === "SVB_Verifiee" ? "green" : "blue",
                //svb: 'green'
              }) as never
            );
          }
        );
        setParcelles(parcelles);
        onParcellesChanged(parcelles);
      }
    };

    if (corners.bl) searchParcelle(corners);
  }, [corners, filters, smsToken, searchLocationAuthenticated]);

  function reloadMarker() {
    const m = marker.split("|");

    if (m[0]) {
      const layers = map.current.getLayers().getArray();
      const searchResultMarkersLayer = layers.find(
        (layer) => layer.get("name") === "searchResultMarkers"
      );

      if (searchResultMarkersLayer) {
        // Do something with the layer, for example, remove it before adding a new one
        map.current.removeLayer(searchResultMarkersLayer);
      }

      // Create a new Vector layer for the marker
      const newLayer = new VectorLayer({
        zIndex: -1,
        source: new VectorSource({
          features: [
            new Feature({
              geometry: new Point(fromLonLat([+m[0], +m[1]])),
            }),
          ],
        }),
        style: new Style({
          image: new CircleStyle({
            radius: 12,
            fill: new Fill({ color: "red" }),
            stroke: new Stroke({ color: "white", width: 3 }),
          }),
        }),
        properties: { name: "searchResultMarkers" },
      });

      // Add the new layer to the map
      map.current.addLayer(newLayer);
    }
  }

  useEffect(() => {
    map.current = new Map({
      target: "map",
      layers: [
        // @ts-ignore: not typed
        new MapboxVectorLayer({
          zIndex: -2,
          styleUrl: "mapbox://styles/melcarne/cm1gsje3x003h01pef5uhgih6",
          accessToken:
            "pk.eyJ1IjoibWVsY2FybmUiLCJhIjoiY20weHZ1cm5sMGM4bzJqcXY2ZW12Z3FjcCJ9.HVobOBbzDba3vcy1ozQOjw",
        }),
        new VectorLayer({
          source: vectorSource.current,
          style: (feature) => {
            return styles[`${feature.get("type")}_${feature.get("svb")}`];
          },
        }),
      ],
      view: new View({
        center: fromLonLat(centerCoordinate),
        zoom,
        minZoom: 15,
      }),
    });

    if (map.current) {
      detailOverlay.current = new Overlay({
        element:
          detailPopup.current !== null
            ? (detailPopup.current as HTMLElement)
            : new HTMLElement(),
      });
      titleOverlay.current = new Overlay({
        element:
          titlePopup.current !== null
            ? (titlePopup.current as HTMLElement)
            : new HTMLElement(),
      });
      map.current.addOverlay(detailOverlay.current);
      map.current.addOverlay(titleOverlay.current);

      map.current.on("pointermove", (evt) => {
        if (!evt.dragging) {
          // Check if there are any features at this pixel for cursor change
          const hasFeatures = map.current.getFeaturesAtPixel(
            map.current.getEventPixel(evt.originalEvent)
          ).some(f => f.get("name") !== undefined);
          
          map.current.getTargetElement().style.cursor = hasFeatures ? "pointer" : "";
        }

        // Get all features at hover point and select the closest one
        const featuresAtPixel: any[] = [];
        map.current.forEachFeatureAtPixel(
          evt.pixel,
          (feature) => {
            if (feature.get("name") !== undefined) {
              featuresAtPixel.push(feature);
            }
          }
        );

        let feature = null;
        if (featuresAtPixel.length > 0) {
          if (featuresAtPixel.length === 1) {
            feature = featuresAtPixel[0];
          } else {
            // Multiple features found - select the one closest to hover point
            let closestFeature = featuresAtPixel[0];
            let closestDistance = Infinity;
            
            featuresAtPixel.forEach((f) => {
              const featureCoord = getFeatureCoordinates(f);
              if (featureCoord) {
                const hoverCoord = evt.coordinate;
                const distance = Math.sqrt(
                  Math.pow(featureCoord[0] - hoverCoord[0], 2) +
                  Math.pow(featureCoord[1] - hoverCoord[1], 2)
                );
                
                if (distance < closestDistance) {
                  closestDistance = distance;
                  closestFeature = f;
                }
              }
            });
            
            feature = closestFeature;
          }
        }

        if (titleOverlay.current)
          if (feature) {
            setHoveredParcelle(feature.get("address"));
            titleOverlay.current.setPosition(evt.coordinate);
            titleOverlay.current.setPositioning("bottom-center");
          } else {
            titleOverlay.current.setPosition(undefined);
          }
      });
      map.current.on("pointerdrag", (_) => {
        setOpenFilters(false);
      });

      map.current.on("click", (evt) => {
        setParcelleStats(null);
        setOpenFilters(false);

        if (titleOverlay.current && detailOverlay.current) {
          titleOverlay.current.setPosition(undefined);
          const view = map.current.getView();
          const extent = view.calculateExtent(map.current.getSize());
          
          // Get all features at click point and select the closest one
          const featuresAtPixel: any[] = [];
          map.current.forEachFeatureAtPixel(
            evt.pixel,
            (feature) => {
              if (feature.get("name") !== undefined) {
                featuresAtPixel.push(feature);
              }
            }
          );

          let feature = null;
          if (featuresAtPixel.length > 0) {
            if (featuresAtPixel.length === 1) {
              feature = featuresAtPixel[0];
            } else {
              // Multiple features found - select the one closest to click point
              let closestFeature = featuresAtPixel[0];
              let closestDistance = Infinity;
              
              featuresAtPixel.forEach((f) => {
                const featureCoord = getFeatureCoordinates(f);
                if (featureCoord) {
                  const clickCoord = evt.coordinate;
                  const distance = Math.sqrt(
                    Math.pow(featureCoord[0] - clickCoord[0], 2) +
                    Math.pow(featureCoord[1] - clickCoord[1], 2)
                  );
                  
                  if (distance < closestDistance) {
                    closestDistance = distance;
                    closestFeature = f;
                  }
                }
              });
              
              feature = closestFeature;
            }
          }

          if (feature && feature.get("name") !== undefined) {
            const [selParcelle, selCommune] = feature.get("name").split('#');

            onSelectParcelle(selParcelle, selCommune);
            setSelectedParcelle(selParcelle);
            setSelectedParcelleNoCommune(selCommune)

            const coordinate = evt.coordinate;
            detailOverlay.current.setPosition(coordinate);
            detailOverlay.current.setPositioning("bottom-center");

            vectorSource.current.forEachFeatureInExtent(
              extent,
              function (feature: Feature) {
                feature.set(
                  "type",
                  `${selParcelle}#${selCommune}` === feature.get("name")
                    ? "geoMarkerSelected"
                    : "geoMarker"
                );
              }
            );

            setCenterCoordinate(
              transform(coordinate, "EPSG:3857", "EPSG:4326")
            );
            //            map.current.getView().setCenter(coordinate);
            map.current.getView().animate({
              center: coordinate,
              duration: 500,
            });
          } else {
            detailOverlay.current.setPosition(undefined);
            onSelectParcelle(null, null);
          }
        }
      });

      map.current.on("moveend", (_) => {
        const view = map.current.getView();
        const extent = view.calculateExtent(map.current.getSize());
        const newZoom = view.getZoom()!;
        const zoomChanged = zoom !== newZoom;
        const newCenter = transform(
          view.getCenter()!,
          "EPSG:3857",
          "EPSG:4326"
        );

        const centerChanged =
          newCenter[0] !== centerCoordinate[0] ||
          newCenter[1] !== centerCoordinate[1];

        if (zoomChanged || centerChanged) {
          setCorners({
            bl: transform(getBottomLeft(extent), "EPSG:3857", "EPSG:4326"),
            br: transform(getBottomRight(extent), "EPSG:3857", "EPSG:4326"),
            tl: transform(getTopLeft(extent), "EPSG:3857", "EPSG:4326"),
            tr: transform(getTopRight(extent), "EPSG:3857", "EPSG:4326"),
          });
          setZoom(newZoom);
          setCenterCoordinate(newCenter);
        }
      });
    }

    map.current.getInteractions().forEach(function(interaction) {
      if (interaction instanceof MouseWheelZoom) {
        map.current.removeInteraction(interaction);
      }
    });
    reloadMarker();

    return () => map.current?.setTarget(undefined);
  }, [zoom]);

  const parcelleData: any[] = parcelles.filter(
    (p: any) => p?.NoParcelle === selectedParcelle
  );

  return (
    <div>
      <div
        style={{
          height: "calc(100vh - 64px)",
          width: "100%",
          //cursor: "pointer",
        }}
        id="map"
        className="map-container"
      >
        <div
          style={{
            position: "fixed",
            bottom: "0",
            right: "0px",
            zIndex: "9999",
            width: "100%",
            height: "30px",
            background: "white",
            display: "flex",
            justifyContent: "space-evenly",
            alignItems: "center",
            color: "black",
            padding: "10px",
            fontSize:  window.self === window.top ? "10px" : "12px"
          }}
        >
          <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <div
              style={{
                minWidth: 12,
                height: 12,
                borderRadius: "50%", // Alternative to 12px borderRadius
                backgroundColor: "#7ED957",
              }}
            />
            PRIX/m² DISPONIBLES ET ANALYSÉS
          </span>
          <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <div
              style={{
                minWidth: 12,
                height: 12,
                borderRadius: "50%", // Alternative to 12px borderRadius
                backgroundColor: "#001482",
              }}
            />
            TRANSACTIONS IMMOBILIERES DE TOUTES LES CATEGORIES D’ACTIFS (12 derniers mois)
          </span>
        </div>
      </div>

      <div ref={detailPopup} id="detail-popup" style={{ marginBottom: 17 }}>
        {parcelleData && parcelleData.length > 0 && (
          <ParcellePopup
            loadingParcellesList={loadingParcellesList}
            setLoadingParcellesList={setLoadingParcellesList}
            showSMSModal={showSMSModal}
            smsToken={smsToken}
            transactions={parcelleData.sort((t1, t2) =>
              t1.Date_Transaction > t2.Date_Transaction ? -1 : 1
            )}
            parcelleStats={parcelleStats}
            setSearchLocationAuthenticated={setSearchLocationAuthenticated}
            searchLocationAuthenticated={searchLocationAuthenticated}
            displayPrice={displayPrice}
            filters={filters}
            remainingCredits={remainingCredits}
            onParcelleDetailsAccess={onParcelleDetailsAccess}
            setOpenTransactionList={setOpenTransactionList}
            openTransactionList={openTransactionList}
          />
        )}
      </div>

      <div ref={titlePopup} id="title-popup" style={{ marginBottom: 12 }}>
        {hoveredParcelle && (
          <>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                backgroundColor: "white",
                padding: 12,
                borderRadius: 8,
                width: 340,
                boxShadow:
                  "rgba(0, 0, 0, 0.25) 0px 14px 28px, rgba(0, 0, 0, 0.22) 0px 10px 10px",
                textTransform: "uppercase",
                fontWeight: "bolder",
                fontSize: 14,
              }}
            >
              {hoveredParcelle}
            </div>
            <div
              style={{
                position: "absolute",
                width: 12,
                height: 12,
                backgroundColor: "white",
                transform: "rotate(45deg)",
                bottom: 8,
                left: "calc(50% - 8px)",
              }}
            ></div>
          </>
        )}
      </div>
    </div>
  );
};

export default MapComponent;

const ParcellePopup: FC<{
  transactions: any[];
  parcelleStats: any;
  showSMSModal: any;
  setSearchLocationAuthenticated: any;
  searchLocationAuthenticated: any;
  smsToken?: string;
  displayPrice: any;
  filters: any;
  remainingCredits: any;
  onParcelleDetailsAccess: any;
  setOpenTransactionList: any;
  openTransactionList: any;
  setLoadingParcellesList: any;
  loadingParcellesList: boolean;
}> = ({
  transactions,
  parcelleStats,
  showSMSModal,
  setSearchLocationAuthenticated,
  searchLocationAuthenticated,
  smsToken,
  displayPrice,
  filters,
  remainingCredits,
  onParcelleDetailsAccess,
  setOpenTransactionList,
  openTransactionList,
  setLoadingParcellesList,
  loadingParcellesList
}) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [oneKmMedianPrice, setOneKmMedianPrice] = useState<string | null>(null);
  const [oneKmMedianSurfacePrice, setOneKmMedianSurfacePrice] = useState<
    string | null
  >(null);
  const [oneKmMedianSurface, setOneKmMedianSurface] = useState<string | null>(
    null
  );
  const [sameStreetMedianSurfacePrice, setSameStreetMedianSurfacePrice] =
    useState<string | null>(null);
  const [sameStreetMedianPrice, setSameStreetMedianPrice] = useState<
    string | null
  >(null);
  const transaction = transactions[selectedIndex];

  useEffect(() => {
    setSelectedIndex(0);
  }, [transactions]);

  useEffect(() => {
    if (transaction && transaction.status === "SVB_Verifiee") {
      fetchMedianPrice(
        transaction?.NoCommune,
        transaction?.NoParcelle,
        transaction?.id
      );
    }
  }, [transaction]);

  const fetchMedianPrice = async (
    noCommune: string,
    noParcelle: string,
    transactionId: number
  ) => {
    try {
      const smsTokenParameter =
        transactionId === searchLocationAuthenticated
          ? `smsToken=${smsToken}`
          : "";
      const response = await axios.get(
        `https://melcarnebackend.digipulse.ch/get_median_prices?NoCommune=${noCommune}&NoParcelle=${noParcelle}&filters=${JSON.stringify(
          filters
        )}&${smsTokenParameter}`
      );
      setOneKmMedianPrice(response.data.one_km_median_price);
      setOneKmMedianSurfacePrice(response.data.one_km_median_surface_price);
      setOneKmMedianSurface(response.data.one_km_median_surface);
      setSameStreetMedianPrice(response.data.street_median_price);
      setSameStreetMedianSurfacePrice(
        response.data.street_median_surface_price
      );
      if (smsTokenParameter != "") {
        setLoadingParcellesList(false);
      }
    } catch (error) {
      console.error("Error fetching median price:", error);
    }
  };

  const displayDate = (inputDate: string) => {
    const [year, month, day] = inputDate.split("-");
    return `${day}/${month}/${year.slice(-2)}`;
  };

  const displayYear = (inputDate: string) => {
    const [year, month, day] = inputDate.split("-");
    return `${year}`;
  };

  return (
    transaction &&
    (smsToken && searchLocationAuthenticated === transaction.id ? ( // Point vert chargé
      <>
        {/* Ceci est la popup du point vert chargé */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            backgroundColor: "white",
            padding: 16,
            borderRadius: 8,
            width: 340,
            boxShadow:
              "rgba(0, 0, 0, 0.25) 0px 14px 28px, rgba(0, 0, 0, 0.22) 0px 10px 10px",
          }}
        >
          <header
            style={{
              display: "flex",
              flexDirection: "column",
              marginBottom: 12,
            }}
          >
            <div
              style={{
                textTransform: "uppercase",
                fontWeight: "bolder",
                fontSize: 14,
                marginBottom: 4,
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <span>{`${transaction.Adresse}, ${transaction.NPA} ${transaction.CommuneAdresse}`}</span>
              <div
                style={{
                  width: 12,
                  height: 12,
                  borderRadius: 12,
                  //backgroundColor: transaction.SVB_Verifiee ? "green" : "red",
                  backgroundColor: "#7ED957",
                }}
              ></div>
            </div>
            <span style={{ marginBottom: 4, fontSize: 11 }}>
              {transaction.Usages}
            </span>
          </header>

          <div style={{ display: "flex", flexDirection: "column" }}>
            <div style={{ display: "flex", marginBottom: 8 }}>
              <div
                style={{
                  display: "flex",
                  width: "50%",
                  borderRight: "1px solid #e0e0e0",
                  justifyContent: "flex-start",
                  flexDirection: "column",
                  fontSize: 11,
                }}
              >
                <span style={{ textTransform: "uppercase" }}>
                  Données de la rue
                </span>
                <div>
                  <PopupField
                    label="Année de la transaction"
                    value={transaction.Year}
                    loading={loadingParcellesList}
                  />

                  <PopupField
                    label="Valeur médiane CHF"
                    value={displayNumber(sameStreetMedianPrice)}
                    loading={loadingParcellesList}
                  />

                  <PopupField
                    label="Valeur médiane CHF/m²"
                    value={displayNumber(sameStreetMedianSurfacePrice)}
                    loading={loadingParcellesList}
                  />
                </div>
              </div>
              <div
                style={{
                  display: "flex",
                  width: "50%",
                  justifyContent: "center",
                  flexDirection: "column",
                  fontSize: 11,
                  paddingLeft: 8,
                }}
              >
                <span style={{ textTransform: "uppercase" }}>
                  Donnée de la zone &nbsp; &nbsp;
                  <Tooltip
                    title={
                      <span style={{ fontSize: 9, lineHeight: 0.75 }}>
                        Données sur 1km², basées sur les filtres
                      </span>
                    }
                    overlayInnerStyle={{ padding: "0 8px", minHeight: 26 }}
                  >
                    <span>
                      <InfoCircleOutlined />
                    </span>
                  </Tooltip>
                </span>

                <PopupField
                  label="Valeur médiane m²"
                  value={displayNumber(oneKmMedianSurface)}
                />

                <PopupField
                  label="Valeur médiane CHF"
                  value={displayNumber(oneKmMedianPrice)}
                />

                <PopupField
                  label="Valeur médiane CHF/m²"
                  value={displayNumber(oneKmMedianSurfacePrice)}
                />
                
              </div>
             
            </div>

            <Button
        style={{
          marginTop: 5,
          zIndex: 300,
          left: 4,
        }}
        
        onClick={() => {

          setOpenTransactionList(!openTransactionList);
        }}
        icon={<DoubleRightOutlined />}
      >Liste des transactions</Button>
          </div>
        </div>
        <div
          style={{
            position: "absolute",
            width: 12,
            height: 12,
            backgroundColor: "white",
            transform: "rotate(45deg)",
            bottom: 12,
            left: "calc(50% - 8px)",
          }}
        > </div>
       
      </>
    ) : transaction.status !== "SVB_Verifiee" ? (
      <>
        {/* Ceci est la popup du point bleu */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            backgroundColor: "white",
            padding: 16,
            borderRadius: 8,
            width: 340,
            boxShadow:
              "rgba(0, 0, 0, 0.25) 0px 14px 28px, rgba(0, 0, 0, 0.22) 0px 10px 10px",
          }}
        >
          <header
            style={{
              display: "flex",
              flexDirection: "column",
              marginBottom: 12,
            }}
          >
            <div
              style={{
                textTransform: "uppercase",
                fontWeight: "bolder",
                fontSize: 14,
                marginBottom: 4,
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <span
                style={{ textTransform: "uppercase", fontWeight: "bold" }}
              >{`${transaction.Adresse}, ${transaction.NPA} ${transaction.CommuneAdresse}`}</span>
              <div
                style={{
                  width: 12,
                  height: 12,
                  borderRadius: 12,
                  //backgroundColor: transaction.SVB_Verifiee ? "green" : "red",
                  backgroundColor: "#001484",
                }}
              ></div>
            </div>
          </header>
          <div
            style={{ display: "flex", flexDirection: "column", gap: "15px" }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <span>{transaction.Usages}</span>

              <span style={{ textAlign: "right" }}>
                {displayYear(transaction.Date_Transaction)}
              </span>
            </div>

            <span></span>

            <span>{displayPrice(transaction.Prix)}</span>
          </div>
        </div>
        <div
          style={{
            position: "absolute",
            width: 12,
            height: 12,
            backgroundColor: "white",
            transform: "rotate(45deg)",
            bottom: 12,
            left: "calc(50% - 8px)",
          }}
        ></div>
      </>
    ) : (
      <>
        {/* Ceci est la popup du point vert non chargé */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            backgroundColor: "white",
            padding: 16,
            borderRadius: 8,
            width: 340,
            boxShadow:
              "rgba(0, 0, 0, 0.25) 0px 14px 28px, rgba(0, 0, 0, 0.22) 0px 10px 10px",
          }}
        >
          <header
            style={{
              display: "flex",
              flexDirection: "column",
              marginBottom: 12,
            }}
          >
            <div
              style={{
                textTransform: "uppercase",
                fontWeight: "bolder",
                fontSize: 14,
                marginBottom: 4,
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <span>{`${transaction.Adresse}, ${transaction.NPA} ${transaction.CommuneAdresse}`}</span>
              <div
                style={{
                  width: 12,
                  height: 12,
                  borderRadius: 12,
                  //backgroundColor: transaction.SVB_Verifiee ? "green" : "red",
                  backgroundColor: "#7ED957",
                }}
              ></div>
            </div>
            <span style={{ marginBottom: 4, fontSize: 11 }}>
              {transaction.Usages}
            </span>
          </header>

          <div style={{ display: "flex", flexDirection: "column" }}>
            <div style={{ display: "flex", marginBottom: 8 }}>
              <div
                style={{
                  display: "flex",
                  width: "50%",
                  borderRight: "1px solid #e0e0e0",
                  justifyContent: "flex-start",
                  flexDirection: "column",
                  fontSize: 11,
                }}
              >
                <span style={{ textTransform: "uppercase" }}>
                  Données de la rue
                </span>
                <div style={{ filter: "blur(3px)" }}>
                  <PopupField label="Année de la transaction" value="1818" />

                  <PopupField
                    label="Valeur médiane CHF"
                    value={displayNumber(1234)}
                  />

                  <PopupField
                    label="Valeur médiane CHF/m²"
                    value={displayNumber(1234)}
                  />
                </div>
              </div>
              <div
                style={{
                  display: "flex",
                  width: "50%",
                  justifyContent: "center",
                  flexDirection: "column",
                  fontSize: 11,
                  paddingLeft: 8,
                }}
              >
                <span style={{ textTransform: "uppercase" }}>
                  Donnée de la zone &nbsp; &nbsp;
                  <Tooltip
                    title={
                      <span style={{ fontSize: 9, lineHeight: 0.75 }}>
                        Données sur 1km², basées sur les filtres
                      </span>
                    }
                    overlayInnerStyle={{ padding: "0 8px", minHeight: 26 }}
                  >
                    <span>
                      <InfoCircleOutlined />
                    </span>
                  </Tooltip>
                </span>

                <PopupField
                  label="Valeur médiane m²"
                  value={displayNumber(oneKmMedianSurface)}
                />

                <PopupField
                  label="Valeur médiane CHF"
                  value={displayNumber(oneKmMedianPrice)}
                />

                <PopupField
                  label="Valeur médiane CHF/m²"
                  value={displayNumber(oneKmMedianSurfacePrice)}
                />
              </div>
            </div>
            <span style={{ marginTop: "10px", marginBottom: "10px" }}>
              {displayPrice(transaction.Prix)}
            </span>
            <div
              style={{
                display: "flex",
                justifyContent: "center", // Center horizontally
                alignItems: "center", // Center vertically
              }}
            >
              {searchLocationAuthenticated !== transaction.id &&
                (remainingCredits >= 1 || remainingCredits === undefined) && (
                  <Button
                    type="primary"
                    onClick={() => {
                      setLoadingParcellesList(true);
                      smsToken
                        ? setSearchLocationAuthenticated(transaction.id)
                        : showSMSModal(transaction.id);
                      onParcelleDetailsAccess();
                    }}
                  >
                    Accéder aux détails
                  </Button>
                )}
            </div>
          </div>
        </div>
        <div
          style={{
            position: "absolute",
            width: 12,
            height: 12,
            backgroundColor: "white",
            transform: "rotate(45deg)",
            bottom: 12,
            left: "calc(50% - 8px)",
          }}
        ></div>
      </>
    ))
  );
};

const PopupField: FC<{ label: string; value: string; loading?: boolean }> = ({ label, value, loading }) => {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        marginTop: 8,
        fontSize: 10,
      }}
    >
      <span
        style={{
          color: "#757575",
          letterSpacing: 1.1,
        }}
      >
        {label}
      </span>
      <span style={{ fontWeight: "bolder", marginTop: 2, fontSize: 13 }}>
        {loading? <Spin indicator={<LoadingOutlined spin />} /> : value}
      </span>
    </div>
  );
};
function defaultInteractions(arg0: { mouseWheelZoom: boolean; }): import("ol").Collection<import("ol/interaction/Interaction").default> | import("ol/interaction/Interaction").default[] | undefined {
  throw new Error("Function not implemented.");
}

